// src/components/ClientAutocompleteInput.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, User, Building2, Mail, Phone } from 'lucide-react';
import apiService from '../../services/api';

const ClientAutocompleteInput = ({ 
    value, 
    onChange, 
    onClientSelect, 
    onCreateNewClient,
    placeholder = "Search for existing client...",
    disabled = false 
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    
    const inputRef = useRef(null);
    const suggestionRefs = useRef([]);
    const searchTimeout = useRef(null);

    useEffect(() => {
        if (value) {
            setSearchTerm(value);
        }
    }, [value]);

    useEffect(() => {
        // Clear previous timeout
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }

        // Debounce search
        if (searchTerm.length >= 2) {
            searchTimeout.current = setTimeout(() => {
                searchClients(searchTerm);
            }, 300);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }

        return () => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }
        };
    }, [searchTerm]);

    const searchClients = async (query) => {
        setIsLoading(true);
        try {
            const response = await apiService.searchClients(query);
            if (response.success) {
                setSuggestions(response.data);
                setShowSuggestions(true);
                setHighlightedIndex(-1);
            }
        } catch (error) {
            console.error('Error searching clients:', error);
            setSuggestions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        onChange?.(value);
        setShowSuggestions(true);
    };

    const handleClientSelect = (client) => {
        setSearchTerm(client.label);
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        onClientSelect?.(client);
    };

    const handleCreateNew = () => {
        setShowSuggestions(false);
        onCreateNewClient?.(searchTerm);
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => 
                    prev < suggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
                    handleClientSelect(suggestions[highlightedIndex]);
                } else if (searchTerm.length >= 2) {
                    handleCreateNew();
                }
                break;
            case 'Escape':
                setShowSuggestions(false);
                setHighlightedIndex(-1);
                break;
        }
    };

    const handleBlur = (e) => {
        // Delay hiding suggestions to allow click events
        setTimeout(() => {
            setShowSuggestions(false);
            setHighlightedIndex(-1);
        }, 150);
    };

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <div style={{ position: 'relative' }}>
                <Search
                    size={20}
                    style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#9ca3af',
                        zIndex: 1
                    }}
                />
                <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    onFocus={() => {
                        if (suggestions.length > 0) {
                            setShowSuggestions(true);
                        }
                    }}
                    placeholder={placeholder}
                    disabled={disabled}
                    style={{
                        width: '100%',
                        padding: '12px 12px 12px 44px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '14px',
                        outline: 'none',
                        backgroundColor: disabled ? '#f9fafb' : 'white',
                        cursor: disabled ? 'not-allowed' : 'text'
                    }}
                />
                {isLoading && (
                    <div style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)'
                    }}>
                        <div style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid #e5e7eb',
                            borderTop: '2px solid #1e40af',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }} />
                    </div>
                )}
            </div>

            {/* Suggestions Dropdown */}
            {showSuggestions && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000,
                    maxHeight: '300px',
                    overflowY: 'auto',
                    marginTop: '4px'
                }}>
                    {suggestions.length > 0 ? (
                        <>
                            {suggestions.map((client, index) => (
                                <div
                                    key={client.id}
                                    ref={el => suggestionRefs.current[index] = el}
                                    onClick={() => handleClientSelect(client)}
                                    style={{
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        borderBottom: index < suggestions.length - 1 ? '1px solid #f3f4f6' : 'none',
                                        backgroundColor: highlightedIndex === index ? '#f3f4f6' : 'transparent',
                                        transition: 'background-color 0.15s ease'
                                    }}
                                    onMouseEnter={() => setHighlightedIndex(index)}
                                >
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '12px'
                                    }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '8px',
                                            backgroundColor: '#eff6ff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}>
                                            {client.companyName ? (
                                                <Building2 size={20} color="#1e40af" />
                                            ) : (
                                                <User size={20} color="#1e40af" />
                                            )}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#1f2937',
                                                marginBottom: '2px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {client.clientName}
                                            </div>
                                            {client.companyName && (
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: '#6b7280',
                                                    marginBottom: '4px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {client.companyName}
                                                </div>
                                            )}
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                fontSize: '12px',
                                                color: '#6b7280'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    <Mail size={12} />
                                                    <span style={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        maxWidth: '150px'
                                                    }}>
                                                        {client.clientEmail}
                                                    </span>
                                                </div>
                                                {client.clientPhone && (
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}>
                                                        <Phone size={12} />
                                                        <span>{client.clientPhone}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            
                            {/* Create New Client Option */}
                            {searchTerm.length >= 2 && (
                                <div
                                    onClick={handleCreateNew}
                                    style={{
                                        padding: '12px 16px',
                                        cursor: 'pointer',
                                        borderTop: '1px solid #e5e7eb',
                                        backgroundColor: '#f9fafb',
                                        transition: 'background-color 0.15s ease'
                                    }}
                                    onMouseEnter={() => setHighlightedIndex(-1)}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.backgroundColor = '#f9fafb';
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        color: '#1e40af'
                                    }}>
                                        <Plus size={16} />
                                        <span style={{ fontSize: '14px', fontWeight: '500' }}>
                                            Create new client "{searchTerm}"
                                        </span>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : searchTerm.length >= 2 ? (
                        <div style={{ padding: '16px', textAlign: 'center' }}>
                            <div style={{
                                fontSize: '14px',
                                color: '#6b7280',
                                marginBottom: '8px'
                            }}>
                                No clients found for "{searchTerm}"
                            </div>
                            <button
                                onClick={handleCreateNew}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '6px 12px',
                                    backgroundColor: '#1e40af',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    cursor: 'pointer'
                                }}
                            >
                                <Plus size={14} />
                                Create New Client
                            </button>
                        </div>
                    ) : null}
                </div>
            )}

            <style jsx>{`
                @keyframes spin {
                    0% { transform: translateY(-50%) rotate(0deg); }
                    100% { transform: translateY(-50%) rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default ClientAutocompleteInput;